import { useEffect, useRef, useState } from 'react';

/** How far (px, after damping) the list must be pulled to refresh. */
export const PULL_THRESHOLD = 64;
/** The pull stops growing here. */
const PULL_MAX = 96;
/** Finger travel to pull distance: the list moves at half the finger. */
const DAMPING = 0.5;

/**
 * Pull down to refresh on a touch screen: a downward drag that starts with
 * the page at the very top. Returns the current pull distance (0 when not
 * pulling); releasing past PULL_THRESHOLD calls onRefresh. Drags that start
 * on the map are left to the map. While mounted, the browser's own
 * pull-to-reload is turned off so the two never both fire.
 */
export const usePullToRefresh = (onRefresh: () => void, enabled = true): number => {
  const [pull, setPull] = useState(0);
  const pullRef = useRef(0);
  const startYRef = useRef<number | null>(null);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return undefined;
    const set = (value: number) => {
      pullRef.current = value;
      setPull(value);
    };
    const onStart = (e: TouchEvent) => {
      const target = e.target as Element | null;
      startYRef.current =
        window.scrollY <= 0 && e.touches.length === 1 && !target?.closest?.('.leaflet-container')
          ? e.touches[0].clientY
          : null;
    };
    const onMove = (e: TouchEvent) => {
      if (startYRef.current === null) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0 || window.scrollY > 0) {
        // Scrolling up, or the page moved: not a pull.
        if (pullRef.current) set(0);
        if (dy < 0) startYRef.current = null;
        return;
      }
      set(Math.min(dy * DAMPING, PULL_MAX));
    };
    const onEnd = () => {
      const pulled = pullRef.current;
      startYRef.current = null;
      if (pulled) set(0);
      if (pulled >= PULL_THRESHOLD) onRefreshRef.current();
    };
    const body = document.body.style;
    const previous = body.overscrollBehaviorY;
    body.overscrollBehaviorY = 'contain';
    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd);
    window.addEventListener('touchcancel', onEnd);
    return () => {
      body.overscrollBehaviorY = previous;
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled]);

  return pull;
};
